// ---------------------------------------------------------------------------
// Access Discovery → events grid node scope. "Show Access Events" hands the grid
// the `access_discovery_facts` keys behind a chart node: `identity_bucket`,
// `to_topic`, `sub_topic` for an unplaced document type, and for a cluster its
// `cluster_level_1..N_id` path plus both tree versions, never its document types.
// `AccessDiscoveryLink` wraps the scope with the Overview time range, the widget's
// service, the grid's pills and one file or identity from an Overview row; it is
// the only way any surface builds an `/access-discovery` URL. Each chip clears one
// axis; labels ride along because the grid has no tree to derive them from.
// ---------------------------------------------------------------------------

import { isChartAsOf } from '@dawn/feature-config';
import {
  isConnectedService,
  isTimeFrameParam,
  sensitivityLevelFor,
  type ConnectedService,
  type TimeFrameParam,
} from '@dawn/feature-config/widgets';
import type { IdentityKind } from '@dawn/ui';

export interface AccessLegitimacyIdentityScope {
  /** `access_discovery_facts.identity_bucket`: `grp:<org group>` | `nhi` | `external`. */
  id: string;
  label: string;
}

export interface AccessLegitimacyClusterScope {
  /** `access_discovery_facts.cluster_level_1..N_id`, level 1 first. */
  path: readonly string[];
  providerVersion: string;
  /** `cluster_tree_version`: level ids restart on every rebuild, so this is part of the key. */
  treeVersion: string;
}

export interface AccessLegitimacyDataScope {
  /** `access_discovery_facts.to_topic`: `top:<Topic>` | `unclassified`. */
  topic: string;
  /** `access_discovery_facts.sub_topic` labels; empty means the whole topic unless `cluster` is set. */
  documentTypes: readonly string[];
  /** One cluster under the topic. Never widened to the topic when present. */
  cluster?: AccessLegitimacyClusterScope;
  /** Breadcrumb-style label for the chip, e.g. `Financial › Invoice`. */
  label: string;
}

export interface AccessLegitimacyScope {
  identity?: AccessLegitimacyIdentityScope;
  data?: AccessLegitimacyDataScope;
  /** Contract the scope was written under; absent from a link a bundle before it made. */
  scopeContract?: number;
}

/** `access_discovery_facts.file_key` + `source`; the label is the file name, for the chip. */
export interface AccessDiscoveryFileLink {
  key: string;
  source: string;
  label: string;
}

/** `access_discovery_facts.principal_key`; the label is the email / id, for the chip. */
export interface AccessDiscoveryPrincipalLink {
  key: string;
  label: string;
}

/** Everything an `/access-discovery` deep link carries. */
export interface AccessDiscoveryLink {
  scope?: AccessLegitimacyScope;
  /** Overview time range; absent means the grid's full 180-day window. */
  timeFrame?: TimeFrameParam;
  /** The widget's service filter; absent (the widget's `all`) means every service. */
  service?: ConnectedService;
  /** A `sensitivity.level` store value or `unclassified`; absent means all (TDEV-1132). */
  sensitivity?: string;
  /** The grid's `user_type` labels; absent means all (TDEV-1116). */
  userTypes?: readonly string[];
  file?: AccessDiscoveryFileLink;
  principal?: AccessDiscoveryPrincipalLink;
  /** The chart's cutoff (ISO 8601): the grid ends its window there and counts what the chart counts. */
  asOf?: string;
  /** The card counts analysed files only, so the grid leaves the others out too. */
  analysedOnly?: boolean;
}

/**
 * The grid's `user_type` label behind each Overview identity count. dw counts
 * HI / NHI / External as distinct actors by `user_type`, so an icon click is
 * the row's scope plus this one label.
 */
export const USER_TYPE_BY_KIND = {
  HI: 'internal human',
  NHI: 'non-human identity',
  External: 'external human',
} as const satisfies Record<IdentityKind, string>;

const PARAM = {
  identity: 'identity',
  identityLabel: 'identityLabel',
  topic: 'topic',
  docType: 'docType',
  clusterPath: 'clusterPath',
  providerVersion: 'providerVersion',
  treeVersion: 'treeVersion',
  dataLabel: 'dataLabel',
  timeFrame: 'timeFrame',
  service: 'service',
  sensitivity: 'sensitivity',
  userType: 'userType',
  file: 'file',
  fileSource: 'fileSource',
  fileLabel: 'fileLabel',
  principal: 'principal',
  principalLabel: 'principalLabel',
  asOf: 'asOf',
  analysedOnly: 'analysedOnly',
  contract: 'contract',
} as const;

const CLUSTER_PARAMS = [PARAM.clusterPath, PARAM.providerVersion, PARAM.treeVersion] as const;

/** The params each removable chip owns; its × strips exactly these. */
const AXIS_PARAMS = {
  identity: [PARAM.identity, PARAM.identityLabel],
  data: [PARAM.topic, PARAM.docType, ...CLUSTER_PARAMS, PARAM.dataLabel],
  // The cutoff ends the window, so it goes with the window's chip.
  timeFrame: [PARAM.timeFrame, PARAM.asOf, PARAM.analysedOnly],
  service: [PARAM.service],
  file: [PARAM.file, PARAM.fileSource, PARAM.fileLabel],
  principal: [PARAM.principal, PARAM.principalLabel],
} as const;

export type AccessDiscoveryLinkAxis = keyof typeof AXIS_PARAMS;

// The API DTO's TAXONOMY_LABEL_PATTERN + TAXONOMY_LABEL_MAX, so a hand-edited
// value is dropped here rather than turned into a 400.
const TAXONOMY_LABEL_PATTERN = /^[a-z_ -]{1,64}$/;
// The DTO's drill-key rules (TDEV-1116), for the same reason.
const DRILL_KEY_MAX = 512;
const FILE_SOURCE_PATTERN = /^[a-z0-9_-]+$/;
const PRINCIPAL_KEY_PATTERN = /^(id|obs):.+/;
const isDrillKey = (value: string | null, pattern?: RegExp): value is string =>
  value !== null &&
  value.length > 0 &&
  value.length <= DRILL_KEY_MAX &&
  (pattern === undefined || pattern.test(value));

export const TRAIL_SEPARATOR = ' › ';

/** A dw fold ("All Other") at any tier: `all-other`, `other<N>:` or `cluother<N>:`. */
export function isFoldNodeId(id: string): boolean {
  return id === 'all-other' || /^(clu)?other\d+:/.test(id);
}

export function parseScope(params: URLSearchParams): AccessLegitimacyScope {
  const scope: AccessLegitimacyScope = {};
  const identity = params.get(PARAM.identity);
  if (identity) {
    scope.identity = { id: identity, label: params.get(PARAM.identityLabel) ?? identity };
  }
  const contract = Number(params.get(PARAM.contract));
  if (Number.isInteger(contract) && contract > 0) scope.scopeContract = contract;
  const topic = params.get(PARAM.topic) ?? '';
  const documentTypes = params.getAll(PARAM.docType).filter((value) => value.length > 0);
  const hasCluster = CLUSTER_PARAMS.some((key) => params.has(key));
  // Any data param keeps the data scope, however incomplete: the API rejects a
  // partial one, where dropping it would widen the grid.
  if (topic || documentTypes.length > 0 || hasCluster) {
    scope.data = { topic, documentTypes, label: params.get(PARAM.dataLabel) ?? topic };
    if (hasCluster) {
      scope.data.cluster = {
        path: params.getAll(PARAM.clusterPath),
        providerVersion: params.get(PARAM.providerVersion) ?? '',
        treeVersion: params.get(PARAM.treeVersion) ?? '',
      };
    }
  }
  return scope;
}

export function serializeScope(scope: AccessLegitimacyScope): URLSearchParams {
  const params = new URLSearchParams();
  if (scope.scopeContract !== undefined) params.set(PARAM.contract, String(scope.scopeContract));
  if (scope.identity) {
    params.set(PARAM.identity, scope.identity.id);
    params.set(PARAM.identityLabel, scope.identity.label);
  }
  if (scope.data) {
    params.set(PARAM.topic, scope.data.topic);
    for (const documentType of scope.data.documentTypes) params.append(PARAM.docType, documentType);
    const { cluster } = scope.data;
    if (cluster) {
      for (const id of cluster.path) params.append(PARAM.clusterPath, id);
      params.set(PARAM.providerVersion, cluster.providerVersion);
      params.set(PARAM.treeVersion, cluster.treeVersion);
    }
    params.set(PARAM.dataLabel, scope.data.label);
  }
  return params;
}

export function serializeAccessDiscoveryLink(link: AccessDiscoveryLink): URLSearchParams {
  const params = serializeScope(link.scope ?? {});
  if (link.timeFrame) params.set(PARAM.timeFrame, link.timeFrame);
  if (link.service) params.set(PARAM.service, link.service);
  if (link.sensitivity) params.set(PARAM.sensitivity, link.sensitivity);
  for (const userType of link.userTypes ?? []) params.append(PARAM.userType, userType);
  if (link.file) {
    params.set(PARAM.file, link.file.key);
    params.set(PARAM.fileSource, link.file.source);
    params.set(PARAM.fileLabel, link.file.label);
  }
  if (link.principal) {
    params.set(PARAM.principal, link.principal.key);
    params.set(PARAM.principalLabel, link.principal.label);
  }
  if (link.asOf) params.set(PARAM.asOf, link.asOf);
  if (link.analysedOnly) params.set(PARAM.analysedOnly, '1');
  return params;
}

/** The URL is untrusted: a value outside its list or pattern is dropped. */
export function parseAccessDiscoveryLink(params: URLSearchParams): AccessDiscoveryLink {
  const link: AccessDiscoveryLink = {};
  const scope = parseScope(params);
  if (scope.identity || scope.data) link.scope = scope;
  const timeFrame = params.get(PARAM.timeFrame);
  if (isTimeFrameParam(timeFrame)) link.timeFrame = timeFrame;
  const service = params.get(PARAM.service);
  if (isConnectedService(service)) link.service = service;
  const sensitivity = params.get(PARAM.sensitivity);
  if (sensitivity && TAXONOMY_LABEL_PATTERN.test(sensitivity)) {
    // A pre-TDEV-1132 link carries the tier id (`highly-confidential`), not its store level.
    link.sensitivity = sensitivityLevelFor(sensitivity) ?? sensitivity;
  }
  const userTypes = params
    .getAll(PARAM.userType)
    .filter((value) => TAXONOMY_LABEL_PATTERN.test(value));
  if (userTypes.length > 0) link.userTypes = userTypes;
  // A file key names a file only next to its source: one without the other is dropped.
  const file = params.get(PARAM.file);
  const fileSource = params.get(PARAM.fileSource);
  if (isDrillKey(file) && isDrillKey(fileSource, FILE_SOURCE_PATTERN)) {
    link.file = { key: file, source: fileSource, label: params.get(PARAM.fileLabel) || file };
  }
  const principal = params.get(PARAM.principal);
  if (isDrillKey(principal, PRINCIPAL_KEY_PATTERN)) {
    link.principal = { key: principal, label: params.get(PARAM.principalLabel) || principal };
  }
  const asOf = params.get(PARAM.asOf);
  if (isChartAsOf(asOf)) link.asOf = asOf;
  if (params.get(PARAM.analysedOnly) === '1') link.analysedOnly = true;
  return link;
}

/** The same params with one chip's axis removed; every other param is kept. */
export function stripScopeAxis(
  params: URLSearchParams,
  axis: AccessDiscoveryLinkAxis
): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of AXIS_PARAMS[axis]) next.delete(key);
  return next;
}

/** Stable identity of a link, for effect deps and query keys. */
export function linkKey(link: AccessDiscoveryLink): string {
  return serializeAccessDiscoveryLink(link).toString();
}

// --- Resolution from the chart's tree -------------------------------------

interface LabeledNode {
  id: string;
  label: string;
}

/** A drill row as the resolver reads it: its kind and, on a cluster, its scope. */
export interface DrillNode extends LabeledNode {
  axis?: 'data_cluster' | 'data_category';
  clusterPath?: readonly string[];
  providerVersion?: string;
  clusterTreeVersion?: string;
}

/** The slice of the widget's `drillDowns` the resolver reads: children per node. */
export type DrillTree = Readonly<Record<string, { categories: readonly DrillNode[] }>>;

export interface ResolveScopeInput {
  /** Identity row the action came from, if any. */
  identityId: string | null;
  /** Data row the action came from, if any. */
  dataContextId: string | null;
  /** The drill trail being shown, outermost first; empty in the default view. */
  trail: readonly LabeledNode[];
  drillDowns: DrillTree;
  identityColumn: readonly LabeledNode[];
  dataContext: readonly LabeledNode[];
}

const labelOf = (rows: readonly LabeledNode[], id: string): string =>
  rows.find((row) => row.id === id)?.label ?? id;

/** The node kind; an API older than `axis` is read by dw's cluster id prefix. */
export function nodeAxis(node: DrillNode): 'data_cluster' | 'data_category' {
  return node.axis ?? (/^clu(other)?\d+:/.test(node.id) ? 'data_cluster' : 'data_category');
}

/** A cluster row's event scope, or null when the snapshot lacks its path or versions. */
export function clusterScopeOf(node: DrillNode): AccessLegitimacyClusterScope | null {
  const { clusterPath, providerVersion, clusterTreeVersion } = node;
  if (!clusterPath || clusterPath.length === 0 || !providerVersion || !clusterTreeVersion) {
    return null;
  }
  return { path: clusterPath, providerVersion, treeVersion: clusterTreeVersion };
}

/** A tier row's data scope under `topic`, or null when the row has none. */
function rowScope(node: DrillNode, topic: string, label: string): AccessLegitimacyDataScope | null {
  if (!hasRowScope(node)) return null;
  if (nodeAxis(node) === 'data_category') return { topic, documentTypes: [node.label], label };
  const cluster = clusterScopeOf(node);
  return cluster === null ? null : { topic, documentTypes: [], cluster, label };
}

/** Whether a tier row names an event filter: never a fold, and a cluster only with its versions. */
function hasRowScope(node: DrillNode): boolean {
  if (isFoldNodeId(node.id)) return false;
  return nodeAxis(node) === 'data_category' || clusterScopeOf(node) !== null;
}

/** The tier the trail stands in: its department, or its cluster, whose row is in the tier above. */
function tierScope(
  trail: readonly LabeledNode[],
  drillDowns: DrillTree
): AccessLegitimacyDataScope | null {
  const root = trail[0]!;
  const label = trail.map((step) => step.label).join(TRAIL_SEPARATOR);
  const [above, tierParent] = [trail[trail.length - 2], trail[trail.length - 1]!];
  if (above === undefined) return { topic: root.id, documentTypes: [], label };
  const node = drillDowns[above.id]?.categories.find((row) => row.id === tierParent.id);
  return node === undefined ? null : rowScope(node, root.id, label);
}

/**
 * A chart surface's events-grid scope, or null when no fact column names it.
 * In a drill view an identity scopes to the tier it is drawn against as well.
 */
export function resolveScopeFromNode(input: ResolveScopeInput): AccessLegitimacyScope | null {
  const { identityId, dataContextId, trail, drillDowns, identityColumn, dataContext } = input;
  if (identityId !== null && isFoldNodeId(identityId)) return null;
  const scope: AccessLegitimacyScope = {};
  if (identityId !== null) {
    scope.identity = { id: identityId, label: labelOf(identityColumn, identityId) };
  }

  if (trail.length === 0) {
    if (dataContextId === null) return scope;
    if (isFoldNodeId(dataContextId)) return null;
    scope.data = {
      topic: dataContextId,
      documentTypes: [],
      label: labelOf(dataContext, dataContextId),
    };
    return scope;
  }

  if (dataContextId === null) {
    const data = tierScope(trail, drillDowns);
    if (data === null) return null;
    return { ...scope, data };
  }

  const tierParent = trail[trail.length - 1]!;
  const node = drillDowns[tierParent.id]?.categories.find((row) => row.id === dataContextId);
  if (node === undefined) return null;
  const label = [...trail.map((step) => step.label), node.label].join(TRAIL_SEPARATOR);
  const data = rowScope(node, trail[0]!.id, label);
  return data === null ? null : { ...scope, data };
}

/**
 * Every chart id `resolveScopeFromNode` gives no scope in the view `trail` shows,
 * so the body withholds Show Access Events on those rows and their pipes.
 */
export function idsWithoutEventScope(
  input: Omit<ResolveScopeInput, 'identityId' | 'dataContextId'>
): Set<string> {
  const { trail, drillDowns, identityColumn, dataContext } = input;
  const ids = new Set(
    [...identityColumn, ...dataContext].map((row) => row.id).filter(isFoldNodeId)
  );
  for (const tier of Object.values(drillDowns)) {
    for (const node of tier.categories) {
      if (!hasRowScope(node)) ids.add(node.id);
    }
  }
  if (trail.length > 0 && tierScope(trail, drillDowns) === null) {
    for (const row of identityColumn) ids.add(row.id);
  }
  return ids;
}
